import { ChangeDetectorRef, Component, Inject, ViewChild } from "@angular/core";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import {
  MatSelect,
  MAT_SELECT_SCROLL_STRATEGY,
} from "@angular/material/select";
import { Overlay, ScrollStrategy } from "@angular/cdk/overlay";
import { Subscription } from "rxjs";
import {
  RuleConfigSaveTarget,
  RuleEngineService,
  convertNamedRuleExpressionToNumbers,
  convertNumberedRuleExpressionToNames,
} from "../services/rule-engine.service";
import {
  ConfirmDialogComponent,
  ConfirmDialogModel,
} from "../confirm-dialog/confirm-dialog.component";
import { DataParserService } from "../services/data-parser.service";

import { Rule, RuleExpression, RuleExpressoins } from "./models/rule-engine";
import { MatDialog } from "@angular/material/dialog";
import { DiffEditorModel } from "ngx-monaco-editor-v2";
import { ExpressionBuilderComponent } from "./expression-builder/expression-builder.component";
import { PredicateFilterComponent } from "./predicate-filter/predicate-filter.component";
import { ToastrService } from "ngx-toastr";
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { CommonService } from "../services/common.service";
import {
  generatePreValidationMessage,
} from "./utils/prevalidation-message.generator";


declare const copyToClipboard: any;

/** ADDED: Rule Builder Version mat-select — reposition instead of app-wide block() to avoid screen freeze. */
export function ruleBuilderVersionSelectScrollStrategyFactory(
  overlay: Overlay
): () => ScrollStrategy {
  return () => overlay.scrollStrategies.reposition();
}

@Component({
  selector: "app-rule-config-component",
  templateUrl: "./rule-config-component.component.html",
  styleUrls: ["./rule-config-component.component.scss"],
  providers: [
    {
      provide: MAT_SELECT_SCROLL_STRATEGY,
      deps: [Overlay],
      useFactory: ruleBuilderVersionSelectScrollStrategyFactory,
    },
  ],
})
export class RuleConfigComponentComponent {
  private static readonly VERSION_SELECT_BODY_CLASS =
    "rule-builder-version-select-open";
  private static readonly VERSION_SELECT_PANEL_CLASS =
    "rule-builder-version-select-panel";

  @ViewChild("versionSelectRef") versionSelectRef!: MatSelect;
  private versionSelectOutsideClickListener: ((event: Event) => void) | null =
    null;

  isclosebuttonclicked: boolean = false
  isPopUp: boolean = false;
  disableBuildExpression: boolean = true;
  isExpressionBuilderPopUp: boolean = false;
  isFirstView: boolean = true;
  opentemplate: boolean = false;
  finalExpression!: string;
  routeGuid: string;
  routeName: string;
  uniqueID: any;
  ruleExecutionStage:string ='Validation';
  totalrecords: number;
  cacheList:any[]=[];
  // Version dropdown options (configdata = RuleConfig for that version; flags used for styling).
  versionOptions: {
    value: string;
    label: string;
    configdata?: any;
    isLive?: boolean;
    status?: string;
    /** Display status in dropdown: 'Live' only for latest live; older live shown as 'Archived'; QC/Dev as-is */
    displayStatus?: string;
    created?: string;
    isLatestLive?: boolean;
    isLatestDevQc?: boolean;
    routeRuleMappingID?: number | null;
  }[] = [];
  selectedVersion: string | null = null;
  // Holds the last built rule expressions so that
  // Save does not need to close the dialog immediately.
  latestRuleExpressions: RuleExpressoins[] | null = null;
  // Controls enabling of "Push to QC" button.
  isSendToQc: boolean = false;
  // Controls enabling of Approve/Reject after QC step.
  isApproveReject: boolean = false;
  // Note text for Push to QC dialog.
  qcNoteContent: string = "";
  // Controls inline QC note overlay visibility.
  showQcNote: boolean = false;
  // Controls read-only Version Notes overlay (opened from Notes button).
  showVersionNotes: boolean = false;
  notesLoading: boolean = false;
  // Compare overlay: partition screen with two version dropdowns and JSON diff.
  showCompareOverlay: boolean = false;
  compareLeftVersion: string | null = null;
  compareRightVersion: string | null = null;
  compareDiffOldModel: DiffEditorModel = { code: '', language: 'json' };
  compareDiffNewModel: DiffEditorModel = { code: '', language: 'json' };
  compareDiffOptions = {
    theme: 'vs',
    language: 'json',
    readOnly: true,
    renderSideBySide: true,
    lineNumbers: true,
    wordWrap: 'on',
    scrollBeyondLastLine: false
  };
  // Simple in-memory audit trail for QC / approval (dummy data).
  qcAudit: { role: string; notes: string; user: string; timestamp: string }[] = [];
  // Tracks whether we're in QC note stage, approval stage, or rejection.
  qcStage: 'qc' | 'approve' | 'reject' | null = null;
  pagerolepermisions: any = {};
  sourceIsTestOrganization = '';
  destinationIsTestOrganization = '';
  testOrgFlagsLoaded = false;
  private _ruleEngineService!: RuleEngineService;
  private _popUpSubscription!: Subscription;
  private _rulesSubjectSubscription!: Subscription;
  private _isExpressionBuilderPopUpSubjectSubscription!: Subscription;
  private _finalRuleExpressionSubjectSubscription!: Subscription;
  private rules: Rule[] = [];
  // private ngxUiLoaderService: NgxUiLoaderService

  /** Pre-Validation: Case Audit message (auto-generated default; user may edit). */
  validationMessage = '';
  private lastAutoGeneratedValidationMessage = '';
  validationMessageManuallyEdited = false;

  constructor(
    private toastr: ToastrService,
    public dialog: MatDialog,
    ruleEngineService: RuleEngineService,
    public dialogRef: MatDialogRef<RuleConfigComponentComponent>,
    private _dataParserService: DataParserService,
    private ngxUiLoaderService: NgxUiLoaderService,
    private commonService: CommonService,
    private _cdr: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) public ruleConfigRequest: any
  ) {
    this._ruleEngineService = ruleEngineService;
  }

  /** Formats API datetime strings using the user-selected timezone (CommonService / localStorage). */
  private formatRuleConfigDateForDisplay(raw: any): string {
    return this.commonService.formatRuleConfigCreatedDateForDisplay(raw);
  }

  /**
   * Populates versionOptions from raw API data and optionally selects
   * a preferred version (e.g. the one just saved/approved).
   */
  private applyVersionList(raw: any[], preferredVersion?: number | string): void {
    const list = raw || [];

    // New route: no versions yet — show "1 Dev" so the dropdown is not empty and user sees first save will create version 1 Dev.
    if (list.length === 0) {
      this.versionOptions = [{
        value: '1',
        label: '1 Dev',
        status: 'Dev',
        isLatestDevQc: true,
        isLatestLive: false,
        configdata: undefined,
        created: '',
        routeRuleMappingID: undefined,
      }];
      this.selectedVersion = '1';
      this.loadConfigForVersion('1');
      return;
    }

    const { options, latestLiveIndex, latestDevQcIndex } = this.buildVersionOptionsFromRaw(list);
    this.versionOptions = options;

    if (this.versionOptions.length === 0) return;

    this.markLatestIndices(latestLiveIndex, latestDevQcIndex);
    this.applyDisplayStatusToVersionOptions();
    const targetValue = this.resolveTargetVersion(preferredVersion, latestLiveIndex);
    this.selectedVersion = targetValue;
    this.loadConfigForVersion(this.selectedVersion);
  }

  private buildVersionOptionsFromRaw(list: any[]): {
    options: typeof this.versionOptions;
    latestLiveIndex: number;
    latestDevQcIndex: number;
  } {
    let latestLiveIndex = -1;
    let latestDevQcIndex = -1;

    const options = list.map((v: any, idx: number) => {
      const rawVersion = v.Version ?? idx + 1;
      const version = rawVersion.toString();
      const status = (v.VersionStatus ?? '').toString().trim();
      const createdRaw = v.CreatedDate ?? '';
      const created = this.formatRuleConfigDateForDisplay(createdRaw);
      const isLive = status && status.toLowerCase() === 'live';
      const isDevOrQc = status && (status.toLowerCase() === 'dev' || status.toLowerCase() === 'qc');

      if (isLive) latestLiveIndex = idx;
      else if (isDevOrQc) latestDevQcIndex = idx;

      const statusText = status ? ` ${status}` : '';
      const dateText = created ? ` (${created})` : '';
      return {
        value: version,
        configdata: v.RuleConfig,
        isLive,
        status,
        created,
        isLatestLive: false,
        isLatestDevQc: false,
        routeRuleMappingID: v.RouteRuleMappingID,
        label: `${version}${statusText}${dateText}`.trim(),
      };
    });

    return { options, latestLiveIndex, latestDevQcIndex };
  }

  private markLatestIndices(latestLiveIndex: number, latestDevQcIndex: number): void {
    if (latestLiveIndex >= 0) this.versionOptions[latestLiveIndex].isLatestLive = true;
    if (latestDevQcIndex >= 0) this.versionOptions[latestDevQcIndex].isLatestDevQc = true;
  }

  private applyDisplayStatusToVersionOptions(): void {
    this.versionOptions.forEach((opt) => {
      const isArchivedLive = opt.isLive && !opt.isLatestLive;
      opt.displayStatus = isArchivedLive ? 'Archived' : (opt.status ?? '');
      if (isArchivedLive) {
        const createdSuffix = opt.created ? ' (' + opt.created + ')' : '';
        opt.label = (opt.value + ' Archived' + createdSuffix).trim();
      }
    });
  }

  private resolveTargetVersion(preferredVersion: number | string | undefined, latestLiveIndex: number): string {
    if (preferredVersion !== undefined && preferredVersion !== null) {
      const targetStr = preferredVersion.toString();
      const preferred = this.versionOptions.find((o) => o.value?.toString() === targetStr);
      if (preferred) return preferred.value.toString();
    }

    if (latestLiveIndex >= 0) return this.versionOptions[latestLiveIndex].value.toString();
    const lastIdx = this.versionOptions.length - 1;
    return this.versionOptions[lastIdx].value.toString();
  }

  ngOnInit(): void {
    //fetch all the rules if present
    this._rulesSubjectSubscription = this._ruleEngineService
      .getAllRules()
      .subscribe({
        next: (data) => {
          // Delivery Mapping hook: detect 0 -> 1 rules before overwriting (create-rules adds via setAllTheRules).
          const previousRuleCount = this.rules?.length ?? 0;

          // --- Existing logic (left as-is; do not alter behavior for other flows) ---
          this.rules = data ?? [];
          if (data != null) {
            this.disableBuildExpression = false;
          }
          // --- End existing logic ---

          /* Delivery Mapping (CIIS): first rule created from empty state — mirror Create Interface default mapping.
           * Prior implementation only checked previousRuleCount===0; also sync finalExpression + CD so the strip updates.
           * Re-map if expression is still empty (covers missed emissions / ordering). */
          const svcExprBefore = String(
            this._ruleEngineService.getCurrentFinalRuleExpression() ?? ""
          ).trim();
          const uiExprBefore = String(this.finalExpression ?? "").trim();
          const expressionUnset = svcExprBefore === "" && uiExprBefore === "";
          const shouldMapDeliveryFirstRule =
            !this.ruleConfigRequest.isView &&
            // this.ruleConfigRequest.componentname === "interface" &&
            this.rules.length === 1 &&
            (previousRuleCount === 0 || expressionUnset);

          // if (
          //   !this.ruleConfigRequest.isView &&
          //   this.ruleConfigRequest.componentname === 'interface' &&
          //   previousRuleCount === 0 &&
          //   this.rules.length === 1
          // ) {
          //   this.prepareJsonFromDefaultRules();
          //   this.buildFinalExpression();
          // }

          if (shouldMapDeliveryFirstRule) {
            this.prepareJsonFromDefaultRules();
            this.buildFinalExpression();
            // Same tick as setFinalRuleExpression — assign so {{ finalExpression }} / getters see the value immediately.
            this.finalExpression =
              this._ruleEngineService.getCurrentFinalRuleExpression();
            this._cdr.detectChanges();
          }

          this.refreshPreValidationMessageIfNeeded();
        },
        error: (err) => {
          this.toastr.error("Unable to fetch Rules", "", {
            timeOut: 4000,
            positionClass: "toast-bottom-right",
          });
        },
      });
      this._popUpSubscription = this._ruleEngineService.$getPopUpValue.subscribe({
        next: (data) => {this.isPopUp = data;
          if(this.isPopUp){
            this.openPredicateDialog();
          }
        },
        error: (err) => {
          this.toastr.error("Unable to open sub conditions", "", {
            timeOut: 4000,
            positionClass: "toast-bottom-right",
          });
        }
      });
    //fetch the final rule expression if present
    this._finalRuleExpressionSubjectSubscription =
      this._ruleEngineService.$getFinalRuleExpression.subscribe({
        next: (data: any) => {
          if (this.isFirstView && this.ruleConfigRequest.isView) {
            this.finalExpression = this.convertTheNumberRuleExpToNamedRuleExp(
              this.ruleConfigRequest.data[0].ruleSatisfaction,
              this.ruleConfigRequest.data[0].rules
            );
          } else {
            this.finalExpression = data;
          }
        },
        error: (err) => {
          this.toastr.error("Unable to fetch Rule Expression", "", {
            timeOut: 4000,
            positionClass: "toast-bottom-right",
          });
        },
      });

    // Stage must be known before loading saved rules (Pre-Validation action / message).
    if (this.ruleConfigRequest.ruleExecutionStage) {
      this.ruleExecutionStage = this.ruleConfigRequest.ruleExecutionStage;
    }

    //If the user is viewing the rules which is already created then we will read the old rules and view them
    if (this.ruleConfigRequest.isView) {
      this.viewSavedRules(this.ruleConfigRequest.data);
    }
    //If the user is creating the new route then we would not have any rules in the database so we will set master rules (if any)
    //and initialise the expression builder state accordingly.
    else {
      this.setMasterRules(this.ruleConfigRequest.data);

      if (this.rules.length > 0) {
        // Build default expression from the automatically created master rules
        this.prepareJsonFromDefaultRules();
        this.buildFinalExpression();
      } else {
        // No rules at all for this route � start Expression Builder with an empty expression
        this.prepareTheJsonForEmptyFinalRuleExpression();
        this.finalExpression = '';
        this._ruleEngineService.setFinalRuleExpression('');
      }
    }

    //this entire if condition will run only in case of viewing the rule and not while creating the new route
    if (this.ruleConfigRequest.isView) {
      //this will run only if the there are rules and the user is viewing the rules for the first time
      if (this.rules.length > 0 && this.isFirstView) {
        this.finalExpression = this.convertTheNumberRuleExpToNamedRuleExp(
          this.ruleConfigRequest.data[0].ruleSatisfaction,
          this.ruleConfigRequest.data[0].rules
        );
        this.prepareJsonFromFinalExp(
          this.finalExpression
        );
        this.buildFinalExpression();
        this.isFirstView = false;
      }
    }
    this.routeGuid = this.ruleConfigRequest.routeGuid;
    this.routeName = this.ruleConfigRequest.routeName;
    this.uniqueID = this.ruleConfigRequest.routeUniqueID;

    this.setupPageRolePermissions();
    if (this.pagerolepermisions.approvefortestaccount) {
      this.applyParentTestOrganizationFlags();
    }

    // Always fetch latest versions when dialog opens so we show latest rules after status updates (e.g. Dev ? QC).
    this._ruleEngineService.GetRuleConfigVersions(this.uniqueID, this.ruleExecutionStage).subscribe({
      next: (data: any) => {
        const raw = Array.isArray(data) ? data : [];

        // If this is a new route (no existing versions) and we have master rules + a built expression,
        // automatically create Version 1 Dev in the database so that the initial state is persisted.
        if (!this.ruleConfigRequest.isView && raw.length === 0 && this.rules.length > 0 && this.finalExpression) {
          this.autoSaveInitialDevVersion();
        } else {
          this.applyVersionList(raw);
        }
      },
      error: () => {
        this.toastr.error("Unable to fetch RuleConfig versions", "", {
          timeOut: 4000,
          positionClass: "toast-bottom-right",
        });
      }
    });

    this._ruleEngineService.setAllTheRules(this.rules);
  }

  /**
   * Sets up pagerolepermisions from rolewithmenus based on componentname (interface/createpipeline).
   * Used to control visibility of Send To QC (qcapprove/pushtoqc) and Approve/Reject (liveapprove) buttons.
   */
  private setupPageRolePermissions(): void {
    try {
      const req = this.ruleConfigRequest;
      const componentname = req?.componentname;

      const roleMenus = JSON.parse(sessionStorage.getItem('rolewithmenus') || '[]');
      const selectedRole = sessionStorage.getItem('SelectedUserRole');
      if (!roleMenus?.length || !selectedRole) return;

      const roleData = roleMenus.find((x: any) => x.RoleName === selectedRole);
      if (!roleData?.menuslist) return;

      let moduleName: string | null = null;
      if (componentname === 'interface') {
        moduleName = 'interface';
      } else if (componentname === 'createpipeline') {
        moduleName = 'createpipeline';
      }
      if (!moduleName) return;

      // let menulist = roleData.menuslist.filter((y: any) => y.MenuComponentName.toLowerCase() === 'interface' && (y.MenuName.toLowerCase() === 'liveapprove' || y.MenuName.toLowerCase() ==='qcapprove'));
      // menulist.forEach((element: any) => {
      //   this.pagerolepermisions[element.MenuName] = true;
      // });

      if (moduleName == 'interface') {
        const menuid = JSON.parse(sessionStorage.getItem('rolewithmenus')).find(x => x.RoleName == sessionStorage.getItem('SelectedUserRole')).menuslist.find(y => y.MenuName == 'interface').MenuId;
        const actionitems = JSON.parse(sessionStorage.getItem('rolewithmenus')).find(x => x.RoleName == sessionStorage.getItem('SelectedUserRole')).menuslist.filter(y => y.ParentMenuId == menuid);
        actionitems.forEach(element => {
          this.pagerolepermisions[element.MenuName] = true;
        });

      } else if (moduleName == 'createpipeline') {
        const menuid = JSON.parse(sessionStorage.getItem('rolewithmenus')).find(x => x.RoleName == sessionStorage.getItem('SelectedUserRole')).menuslist.find(y => y.MenuName == 'createpipeline').MenuId;
        const actionitems = JSON.parse(sessionStorage.getItem('rolewithmenus')).find(x => x.RoleName == sessionStorage.getItem('SelectedUserRole')).menuslist.filter(y => y.ParentMenuId == menuid);
        actionitems.forEach(element => {
          this.pagerolepermisions[element.MenuName] = true;
        });
      }

    } catch {
      // Ignore - pagerolepermisions stays empty, buttons will be hidden
    }
  }

  private loadConfigForVersion(version: string): void {
    const option = this.versionOptions.find((o) => o.value === version);
    this.updateActionButtonsForStatus(option?.status);
    const configdata = option?.configdata;
    if (configdata == null) {
      if (this.isPreValidationStage) {
        this.resetPreValidationFields();
      }
      return;
    }

    let parsed: any = configdata;
    if (typeof configdata === 'string') {
      try {
        parsed = JSON.parse(configdata);
      } catch {
        return;
      }
    }

    const dataArray = Array.isArray(parsed) ? parsed : [parsed];
    if (dataArray.length === 0 || !dataArray[0]?.rules) {
      if (this.isPreValidationStage) {
        this.resetPreValidationFields();
      }
      return;
    }

    this.rules = [];
    this.ruleConfigRequest.data = dataArray;
    this.viewSavedRules(this.ruleConfigRequest.data);
    this.prepareJsonFromFinalExp(this.finalExpression);
    this.buildFinalExpression();
    this._ruleEngineService.setAllTheRules(this.rules);
  }

  /** Sets isSendToQc / isApproveReject booleans based on a version status string. */
  private updateActionButtonsForStatus(status?: string): void {
    const vStatus = (status ?? '').toString().trim().toLowerCase();
    if (vStatus === 'dev') {
      this.isSendToQc = true;
      this.isApproveReject = false;
    } else if (vStatus === 'qc') {
      this.isSendToQc = false;
      this.isApproveReject = true;
    } else {
      this.isSendToQc = false;
      this.isApproveReject = false;
    }
  }

  onVersionChange(event: any): void {
    const version = event?.value ?? this.selectedVersion;
    if (version == null || version === '') return;

    const option = this.versionOptions.find((o) => o.value === version);
    const configdata = option?.configdata;

     // Set buttons based on selected version status
    this.updateActionButtonsForStatus(option?.status);

    if (configdata == null) {
      this.toastr.warning("No rule config for this version", "", {
        timeOut: 3000,
        positionClass: "toast-bottom-right",
      });
      return;
    }

    let parsed: any = configdata;
    if (typeof configdata === 'string') {
      try {
        parsed = JSON.parse(configdata);
      } catch {
        this.toastr.error("Invalid rule config data", "", {
          timeOut: 3000,
          positionClass: "toast-bottom-right",
        });
        return;
      }
    }

    const dataArray = Array.isArray(parsed) ? parsed : [parsed];
    if (dataArray.length === 0 || !dataArray[0]?.rules) {
      this.toastr.warning("No rules in this version", "", {
        timeOut: 3000,
        positionClass: "toast-bottom-right",
      });
      return;
    }

    this.rules = [];
    this.ruleConfigRequest.data = dataArray;
    this.viewSavedRules(this.ruleConfigRequest.data);
    this.prepareJsonFromFinalExp(this.finalExpression);
    this.buildFinalExpression();
    this._ruleEngineService.setAllTheRules(this.rules);

    this.toastr.success("Rule config loaded for selected version", "", {
      timeOut: 2000,
      positionClass: "toast-bottom-right",
    });
  }

  //#region ng destroy
  ngOnDestroy(): void {
    if (this._popUpSubscription) {
      this._popUpSubscription.unsubscribe();
    }
    if (this._rulesSubjectSubscription) {
      this._rulesSubjectSubscription.unsubscribe();
    }
    if (this._isExpressionBuilderPopUpSubjectSubscription) {
      this._isExpressionBuilderPopUpSubjectSubscription.unsubscribe();
    }
    if (this._finalRuleExpressionSubjectSubscription) {
      this._finalRuleExpressionSubjectSubscription.unsubscribe();
    }
    this._ruleEngineService.setAllTheRules([]);
    this._ruleEngineService.setFinalRuleExpression("");
    this._dataParserService.setJsonDataForMatTree(null);
    this._ruleEngineService.setFileName("");
    this.opentemplate = false;
    this.cleanupVersionSelectOverlayState();
  }
  //#endregion

  get qcActionLabel(): string {
    switch (this.qcStage) {
      case 'qc':
        return 'Send to QC';
      case 'approve':
        return 'Approve';
      case 'reject':
        return 'Reject';
      default:
        return '';
    }
  }

  get dialogTitle(): string {
    // return 'Rule Builder';
    return this.ruleConfigRequest?.dialogTitle || 'Rule Builder';
  }

  /** True when this dialog is configuring Pre-Validation rules. */
  get isPreValidationStage(): boolean {
    return (
      (this.ruleExecutionStage ?? '').toString().trim().toLowerCase() ===
      'prevalidation'
    );
  }

  onValidationMessageInput(): void {
    this.validationMessageManuallyEdited =
      (this.validationMessage ?? '').trim() !==
      (this.lastAutoGeneratedValidationMessage ?? '').trim();
  }

  /** Regenerates the default validation message when the user has not customized it. */
  private refreshPreValidationMessageIfNeeded(): void {
    if (!this.isPreValidationStage) {
      return;
    }
    if (this.validationMessageManuallyEdited) {
      return;
    }
    const generated = generatePreValidationMessage(this.rules);
    this.lastAutoGeneratedValidationMessage = generated;
    this.validationMessage = generated;
  }

  private loadPreValidationFieldsFromConfig(configBlock: any): void {
    if (!this.isPreValidationStage || !configBlock) {
      return;
    }
    const savedMessage = (configBlock.validationMessage ?? '').toString();
    this.validationMessage = savedMessage;
    const autoFromRules = generatePreValidationMessage(this.rules);
    this.lastAutoGeneratedValidationMessage = autoFromRules;
    // Treat non-empty saved text that differs from current auto text as a manual edit
    this.validationMessageManuallyEdited =
      savedMessage.trim().length > 0 &&
      savedMessage.trim() !== autoFromRules.trim();
    if (!savedMessage.trim() && this.rules.length) {
      this.validationMessageManuallyEdited = false;
      this.refreshPreValidationMessageIfNeeded();
    }
  }

  private resetPreValidationFields(): void {
    this.validationMessage = '';
    this.lastAutoGeneratedValidationMessage = '';
    this.validationMessageManuallyEdited = false;
  }

  /**
   * When multiple rules exist: show the composite preview (name inside parentheses),
   * the "Rule Expression:" label, and the Build Expression control.
   * Single rule: strip hidden everywhere (including Delivery Mapping). First-rule auto-mapping still runs in
   * getAllRules → prepareJsonFromDefaultRules + buildFinalExpression (not shown in UI until 2+ rules).
   */
  get showRuleExpressionLabel(): boolean {
    /* Previous: also showed strip for Delivery Mapping + 1 rule so {{ finalExpression }} was visible.
     * Requirement: display Rule Expression UI only when more than one rule; keep in-memory mapping for first rule.
    const showForMultipleRules = this.rules.length > 1;
    const showDeliveryMappingSingleMappedRule =
      this.ruleConfigRequest?.componentname === 'interface' &&
      this.rules.length === 1 &&
      String(this.finalExpression ?? '').trim() !== '';
    return showForMultipleRules || showDeliveryMappingSingleMappedRule;
    */
    return this.rules.length > 1;
  }

  /** Status of the currently selected version (e.g. 'dev', 'qc', 'live'). Use with isSendToQc / isApproveReject in template. */
  get selectedVersionStatus(): string {
    const option = this.getSelectedVersionOption();
    const status = option?.status;
    return (status ?? '').toString().trim().toLowerCase();
  }

  /** Display string for the selected version (e.g. "Version 3 � QC") for the Version Notes overlay header. */
  get selectedVersionDisplay(): string {
    if (this.selectedVersion == null || !this.versionOptions.length) return '';
    const option = this.versionOptions.find(
      (o) => o.value?.toString() === this.selectedVersion?.toString()
    );
    if (!option) return `Version ${this.selectedVersion}`;
    const parts = [option.value];
    if (option.status) parts.push(option.status);
    if (option.created) parts.push(`(${option.created})`);
    return parts.join(' - ');
  }

  onClickBuildExpression() {
    if (this.ruleConfigRequest.isView) {
      // if (this.rules.length > 0 && this.isNotFirstView) {
      //   this.prepareTheJsonUsingTheFinalExpressionToSendToTheExpressionBuilderComponent(
      //     this.finalExpression
      //   );
      // }
      if (this.rules.length > 0 && this.isFirstView) {
          this.prepareJsonFromFinalExp(
          this.ruleConfigRequest.data[0].ruleSatisfaction
        );
        // this.prepareJsonFromFinalExp(this.finalExpression);
      }
      this.buildFinalExpression();
      // this.isNotFirstView = true;
      this.isFirstView = false;
      this.isExpressionBuilderPopUp = true;
    } 
    else if (!this.ruleConfigRequest.isView) {
      // this.prepareTheJsonToSendToTheExpressionBuilderComponent();
      // this.buildFinalExpression();
      this.isExpressionBuilderPopUp = true;
    }

    if (this.isExpressionBuilderPopUp) {
      const dialogRef = this.dialog.open(ExpressionBuilderComponent, {
        height: "85%",
        width: "90%",
        maxHeight: "18vw !important",
        data: {},
        disableClose: true,
        panelClass: "custom-dialog-container",
        autoFocus: false,
      });
      dialogRef.afterClosed().subscribe((result) => {
        this.isExpressionBuilderPopUp = false; // Reset flag after closing
      });
    }
  }

  setMasterRules(ruleConfigRequest: any) {
    let ruleNumber = 1;
    if (ruleConfigRequest.length > 0) {
      ruleConfigRequest.forEach((rule: any) => {
        if (rule.EntityType + rule.TypeName === "FacilityOrganization") {
          let createdRule :Rule = {
            rulenumber: ruleNumber++,
            rulename: rule.EntityType + rule.TypeName,
            value: rule.Guid,
            field:
              "Case/OrderingProvider/Location[@AccountType='Ordering Facility']/@OrganizationGUID",
            operator: "IN",
          };
          this.rules.push(createdRule);
        } else if (rule.EntityType + rule.TypeName === "FacilityAccount") {
          let createdRule :Rule = {
            rulenumber: ruleNumber++,
            rulename: rule.EntityType + rule.TypeName,
            value: rule.Guid,
            field:
              "Case/OrderingProvider/Location[@AccountType='Ordering Facility']/@GUID",
            operator: "IN",
          };
          this.rules.push(createdRule);
        } else if (
          rule.EntityType + rule.TypeName ===
          "LaboratoryOrganization"
        ) {
          let createdRule :Rule = {
            rulenumber: ruleNumber++,
            rulename: rule.EntityType + rule.TypeName,
            value: rule.Guid,
            field:
              "Case/ServiceProvider/Location[@AccountType='Laboratory']/@OrganizationGUID",
            operator: "IN",
          };
          this.rules.push(createdRule);
        } else if (rule.EntityType + rule.TypeName === "LaboratoryAccount") {
          let createdRule :Rule = {
            rulenumber: ruleNumber++,
            rulename: rule.EntityType + rule.TypeName,
            value: rule.Guid,
            field:
              "Case/ServiceProvider/Location[@AccountType='Laboratory']/@GUID",
            operator: "IN",
          };
          this.rules.push(createdRule);
        }
        else if(rule.EntityType + rule.TypeName === "Send OutAccount"){
          let createdRule :Rule = {
            rulenumber: ruleNumber++,
            rulename: rule.EntityType + rule.TypeName,
            value: rule.Guid,
            field:
              "Case/ServiceProvider[@Type='Laboratory']/Location[@AccountType='Send-out Laboratory']/@GUID",
            operator: "IN",
          };
          this.rules.push(createdRule);
        }
        else if(rule.EntityType + rule.TypeName === "Send InAccount"){
          let createdRule : Rule = {
            rulenumber: ruleNumber++,
            rulename: rule.EntityType + rule.TypeName,
            value: rule.Guid,
            field:
              "Case/ServiceProvider[@Type='Laboratory']/Location[@AccountType='Send-in Laboratory']/@GUID",
            operator: "IN",
          };
          this.rules.push(createdRule);
        }
        else if(rule.EntityType + rule.TypeName === "Send OutOrganization"){
          let createdRule : Rule= {
            rulenumber: ruleNumber++,
            rulename: rule.EntityType + rule.TypeName,
            value: rule.Guid,
            field:
              "Case/ServiceProvider[@Type='Laboratory']/Location[@AccountType='Send-out Laboratory']/@OrganizationGUID",
            operator: "IN",
          };
          this.rules.push(createdRule);
        }
        else if(rule.EntityType + rule.TypeName === "Send InOrganization"){
          let createdRule :Rule = {
            rulenumber: ruleNumber++,
            rulename: rule.EntityType + rule.TypeName,
            value: rule.Guid,
            field:
              "Case/ServiceProvider[@Type='Laboratory']/Location[@AccountType='Send-in Laboratory']/@OrganizationGUID",
            operator: "IN",
          };
          this.rules.push(createdRule);
        }
        else if(rule.EntityType + rule.TypeName === "EPGAccount"){
          let createdRule :Rule = {
            rulenumber: ruleNumber++,
            rulename: rule.EntityType + rule.TypeName,
            value: rule.Guid,
            field:
              "Case/ReportingProvider[@Type='Pathologist']/Location[@AccountType='Pathologist Account']/@GUID",
            operator: "IN",
          };
          this.rules.push(createdRule);
        }
        else if(rule.EntityType + rule.TypeName === "EPGOrganization"){
          let createdRule : Rule= {
            rulenumber: ruleNumber++,
            rulename: rule.EntityType + rule.TypeName,
            value: rule.Guid,
            field:
              "Case/ReportingProvider[@Type='Pathologist']/Location[@AccountType='Pathologist Account']/@OrganizationGUID",
            operator: "IN",
          };
          this.rules.push(createdRule);
        }
      });
    }
  }

  private getAllRulesAsRuleExpresions(): RuleExpression[] {
    const operationMap: { [key: string]: string } = {
      'NOT IN': 'notin',
      'IN': 'in',
      'CONTAINS': 'contains'
    };
    let ruleExpressions: RuleExpression[] = this.rules.map((rule: Rule) => {
      let transformedOperation = operationMap[rule.operator.toLowerCase()] || rule.operator.toLowerCase().split(" ").join("");
      let ruleExpression: RuleExpression = {
        rulenumber: rule.rulenumber,
        field: rule.field,
        operation: transformedOperation,
        delimiter: "|",
        value: rule.value,
        rulename: rule.rulename,
        ruletype: rule.ruletype ?? '',
        param: rule.param ?? ''
      };
      return ruleExpression;
    });
    return ruleExpressions;
  }
  /**
   * Builds the rule expression payload that callers expect.
   * Returns null if the final expression is not yet created.
   */
  private buildRuleExpressionsPayload(): RuleExpressoins[] | null {
    // Single-rule case: Rule Expression / Build Expression row is hidden (showRuleExpressionLabel). Map
    // ruleSatisfaction to that one rule (auto-generated default or first user-created) before validation.
    if (
      this.rules.length === 1 &&
      (this.finalExpression === null ||
        this.finalExpression === undefined ||
        String(this.finalExpression).trim() === '')
    ) {
      this.prepareJsonFromDefaultRules();
      this.buildFinalExpression();
    }

    if (this.finalExpression === null || this.finalExpression === '') {
      this.toastr.warning("Please create rule expression", "", {
        timeOut: 4000,
        positionClass: "toast-bottom-right",
      });
      return null;
    }

    const result = this.getAllRulesAsRuleExpresions();
    const finalNamedRuleExp = this.convertTheNamedRuleExpToNumberRuleExp(
      this.finalExpression,
      this.rules
    );

    const ruleExpressions: RuleExpressoins[] = [
      {
        rules: result,
        ruleSatisfaction: finalNamedRuleExp,
        ...(this.isPreValidationStage
          ? {
              validationMessage: (this.validationMessage ?? '').trim(),
            }
          : {}),
      },
    ];

    return ruleExpressions;
  }

  onClickSaveold() {
    if(this.finalExpression === null || this.finalExpression === ''){
      this.toastr.warning("Please create rule expression", "", {
        timeOut: 4000,
        positionClass: "toast-bottom-right",
      });
    }
    else{
      let result = this.getAllRulesAsRuleExpresions();
      let finalNamedRuleExp = this.convertTheNamedRuleExpToNumberRuleExp(this.finalExpression, this.rules)
      let ruleExpressions: RuleExpressoins[] = [
        {
          rules: result,
          ruleSatisfaction: finalNamedRuleExp,
        },
      ];
      // Attach a non-breaking flag property to the array for consumers who need it
      (ruleExpressions as any).isCloseButtonClicked = this.isclosebuttonclicked === true;
      this.dialogRef.close(ruleExpressions);
    }
    //Here we are clearing the cache of the route to reflect the latest changes.
    this.getAllTheCache(this.routeGuid);
  }

  async onClickSave(): Promise<void> {
    if (this.isPreValidationStage) {
      if (!(this.validationMessage ?? '').trim()) {
        this.toastr.warning("Please enter a Validation Message", "", {
          timeOut: 4000,
          positionClass: "toast-bottom-right",
        });
        return;
      }
    }

    const payload = this.buildRuleExpressionsPayload();
    if (!payload) {
      return;
    }

    const saveTarget = this._ruleEngineService.resolveRuleConfigSaveTarget(
      this.versionOptions
    );
    if (
      this._ruleEngineService.shouldConfirmDevQcOverwriteOnSave(
        this.selectedVersion,
        saveTarget
      ) &&
      !(await this.confirmDevQcOverwriteBeforeSave(saveTarget))
    ) {
      return;
    }

    this.persistRuleConfigSave(payload, saveTarget);
  }

  private async confirmDevQcOverwriteBeforeSave(
    saveTarget: RuleConfigSaveTarget
  ): Promise<boolean> {
    const selectedOption = this.versionOptions.find(
      (o) => o.value?.toString() === this.selectedVersion?.toString()
    );
    const overwriteLabels =
      this._ruleEngineService.buildRuleConfigOverwriteConfirmLabels(
        this.selectedVersion,
        selectedOption?.displayStatus ?? selectedOption?.status,
        saveTarget.targetVersion,
        saveTarget.targetStatus
      );
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      maxWidth: '480px',
      panelClass: 'rule-config-overwrite-confirm-panel',
      data: new ConfirmDialogModel(
        'Confirm Save',
        '',
        'confirm',
        '',
        overwriteLabels
      ),
      disableClose: true,
    });
    return (await dialogRef.afterClosed().toPromise()) === true;
  }

  private persistRuleConfigSave(
    payload: RuleExpressoins[],
    saveTarget: RuleConfigSaveTarget
  ): void {
    const {
      targetVersion,
      targetStatus,
      isSameStatusUpdate,
      isNewRuleConfigVersion,
    } = saveTarget;
    const isLiveFlag = false;

    const body = {
      RouteUniqueId: this.uniqueID,
      RouteGuid: this.routeGuid,
      RuleConfig: JSON.stringify(payload),
      RuleExecutionStage: this.ruleExecutionStage,
      Version: targetVersion,
      VersionStatus: targetStatus,
      IsLive: isLiveFlag,
      UserID: JSON.parse(sessionStorage.getItem('sessionObjectlogin') || 'null')?.userdata?.userid,
    };

    this.ngxUiLoaderService.start();

    this._ruleEngineService.InsertAndUpdateRuleConfig(body).subscribe({
      next: () => {
        // Mark as a normal save (dialog remains open) and cache payload.
        Object.assign(payload, { isCloseButtonClicked: false });
        this.latestRuleExpressions = payload;
        const auditLabel = this.getAuditRuleConfigLabel();
        if (isSameStatusUpdate) {
          this.commonService.auditsave(
            'Update',
            '',
            `${auditLabel} version is updated- ${targetVersion} ${targetStatus} `,
            'Pipeline',
            this.uniqueID,
            `${auditLabel} Version Updated Successfully`
          ).then(() => { }).catch(() => { });
        } else if (isNewRuleConfigVersion) {
          this.commonService.auditsave(
            'Create',
            '',
            `New ${auditLabel} version is Created-${targetVersion} ${targetStatus}`,
            'Pipeline',
            this.uniqueID,
            `${auditLabel} Version Created Successfully.`
          ).then(() => { }).catch(() => { });
        }
        this.isSendToQc = true;
        this.isApproveReject = false;

        this.toastr.success("RuleConfig saved successfully.", "", {
          timeOut: 2000,
          positionClass: "toast-bottom-right",
        });

        // Refresh versions so any insert/update reflects in dropdown
        // and select the just-saved version.
        this._ruleEngineService
          .GetRuleConfigVersions(this.uniqueID, this.ruleExecutionStage)
          .subscribe({
            next: (data: any) => {
              const raw = Array.isArray(data) ? data : [];
              this.applyVersionList(raw, targetVersion);
              this.ngxUiLoaderService.stop();
            },
            error: () => {
              this.ngxUiLoaderService.stop();
              this.toastr.error(
                "Saved, but unable to refresh versions.",
                "",
                {
                  timeOut: 3000,
                  positionClass: "toast-bottom-right",
                }
              );
            },
          });
      },
      error: () => {
        this.ngxUiLoaderService.stop();
        this.toastr.error("Unable to save RuleConfig.", "", {
          timeOut: 4000,
          positionClass: "toast-bottom-right",
        });
      },
    });
  }

  /**
   * Automatically creates Version 1 Dev for new routes that have master rules
   * but no existing RuleConfig versions. This runs once on initial load so that
   * the default master-rule expression is persisted without requiring an explicit Save click.
   */
  private autoSaveInitialDevVersion(): void {
    const payload = this.buildRuleExpressionsPayload();
    if (!payload) {
      return;
    }

    const body = {
      RouteUniqueId: this.uniqueID,
      RouteGuid: this.routeGuid,
      RuleConfig: JSON.stringify(payload),
      RuleExecutionStage: this.ruleExecutionStage,
      Version: 1,
      VersionStatus: 'Dev',
      IsLive: false,
    };

    this.ngxUiLoaderService.start();

    this._ruleEngineService.InsertAndUpdateRuleConfig(body).subscribe({
      next: () => {
        Object.assign(payload, { isCloseButtonClicked: false });
        this.latestRuleExpressions = payload;
        this.isSendToQc = true;
        this.isApproveReject = false;

        // After auto-save, refresh versions so dropdown reflects the real Version 1 Dev from DB.
        this._ruleEngineService
          .GetRuleConfigVersions(this.uniqueID, this.ruleExecutionStage)
          .subscribe({
            next: (data: any) => {
              const raw = Array.isArray(data) ? data : [];
              this.applyVersionList(raw, 1);
              this.ngxUiLoaderService.stop();
            },
            error: () => {
              this.ngxUiLoaderService.stop();
              this.toastr.error(
                "Initial RuleConfig saved, but unable to refresh versions.",
                "",
                {
                  timeOut: 3000,
                  positionClass: "toast-bottom-right",
                }
              );
            },
          });
      },
      error: () => {
        this.ngxUiLoaderService.stop();
        this.toastr.error("Unable to save initial RuleConfig.", "", {
          timeOut: 4000,
          positionClass: "toast-bottom-right",
        });
      },
    });
  }

  getAllTheCache(routeGuid: string) {
    const obj = {
    pageNumber: 1,
    pageSize: 100,
    keysearch: routeGuid
  };

  this.ngxUiLoaderService.start();
  this._ruleEngineService.getCoreCache(obj).subscribe({
    next: (data) => {
      if (data.length > 0) {
        data.forEach((V, index) => {
          let obj = {};
          obj['key'] = V["key"];
          // obj['ischecked'] = false;
          this.totalrecords = V["count"];
          // if (this.pagenumber > 0 && this.paginator != undefined) {
          //   this.paginator.length = this.totalrecords;
          // }
          this.ngxUiLoaderService.stop();
          this.cacheList.push(obj);
        });
        this.clearCache();
      } else {
        this.ngxUiLoaderService.stop();
        // this.toastr.error("Cache data not found", "", {
        //   timeOut: 4000,
        //   positionClass: 'toast-bottom-right'
        // });
        this.cacheList = [];
      }
    },

    error: () => {
      this.ngxUiLoaderService.stop();
      this.toastr.error("Error Occurred", "", {
        timeOut: 4000,
        positionClass: 'toast-bottom-right'
      });
      this.cacheList = [];
    }
  });
}

  clearCache(){
    // let filteredData=this.cacheList.filter(V=>V.ischecked==true);
    // let newdata=this.cacheList.filter(V=>V.ischecked!=true);
    let finalString='';
    this.cacheList.forEach(V=>{
      finalString+=V['key']+',';
    });
    this.ngxUiLoaderService.start();
    const keysToDelete = this.cacheList.map(V => V['key']);
    this._ruleEngineService.getClearCache(keysToDelete).subscribe({
      next: () => {
        // if(data["status"]=='Success'){
        //   this.cacheList=newdata;
        //   this.toastr.success("Re-Populated Successfully", "", {
        //     timeOut: 4000,
        //     positionClass: 'toast-bottom-right'
        //   });
        // } else{
        //   this.toastr.error("Failed to re-populate", "", {
        //     timeOut: 4000,
        //     positionClass: 'toast-bottom-right'
        //   });
        // }
      },
      error: () => {
        this.ngxUiLoaderService.stop();
        // this.toastr.error("Error Occured", "", {
        //   timeOut: 4000,
        //   positionClass: 'toast-bottom-right'
        // });
      }
    });
    this.ngxUiLoaderService.stop();
  }

  OnClickClose() {
    this.isclosebuttonclicked = true;
    if (this.ruleConfigRequest.isView) {
      this.dialogRef.close();
    } else {
      // When rules are empty, user intent is to close the dialog.
      // Avoid calling `buildRuleExpressionsPayload()` because it shows a toast warning.
      if (!this.rules || this.rules.length === 0) {
        this.dialogRef.close(null);
        return;
      }

      // If user closes without explicitly saving, attempt to build payload.
      const payload =
        this.latestRuleExpressions ?? this.buildRuleExpressionsPayload();

      if (!payload) {
        // Validation already shown inside buildRuleExpressionsPayload.
        return;
      }

      // Mark that the close button triggered this, so callers
      // who inspect this flag can behave accordingly.
      const option = this.getSelectedVersionOption();
      const versionInfo = {
        selectedVersion: this.selectedVersion,
        selectedVersionStatus: option?.status ?? '',
        ruleExecutionStage: this.ruleExecutionStage || ''
      };
      Object.assign(payload, { isCloseButtonClicked: true, _versionMeta: versionInfo });

      this.dialogRef.close(payload);
    }
  }

  private addAuditEntry(role: string, notes: string) {
    const user =
      JSON.parse(sessionStorage.getItem('sessionObjectlogin') || 'null')
        ?.userdata?.userid || 'current.user';
    const timestamp = new Date().toLocaleString();
    this.qcAudit.unshift({ role, notes, user, timestamp });
  }

  private getAuditRuleConfigLabel(): string {
    const stage = (this.ruleExecutionStage ?? '').toString().trim().toLowerCase();
    if (stage === 'delivered') {
      return 'Predelivery Rule Config';
    }
    if (stage === 'prevalidation') {
      return 'Pre-Validation Rule Config';
    }
    return 'RuleConfig';
  }

  /** Returns the version option for the currently selected version. */
  private getSelectedVersionOption(): { routeRuleMappingID?: number | null; status?: string } | undefined {
    if (this.selectedVersion == null) return undefined;
    return this.versionOptions.find(
      (o) => o.value?.toString() === this.selectedVersion?.toString()
    );
  }

  /**
   * Common flow: update rule config (InsertAndUpdateRuleConfig) then save version note (InsertRuleConfigVersionNotes).
   * Used for both QC and Approve; role and version params determine the API payload and post-success behaviour.
   */
  private updateRuleConfigAndSaveNote(params: {
    payload: any;
    version: number;
    versionStatus: string;
    role: string;
    onAfterNoteSaved: () => void;
    errorMessage?: string;
  }): void {
    const { payload, version, versionStatus, role, onAfterNoteSaved, errorMessage } = params;
    const body = {
      RouteUniqueId: this.uniqueID,
      RouteGuid: this.routeGuid,
      RuleConfig: JSON.stringify(payload),
      RuleExecutionStage: this.ruleExecutionStage,
      Version: version,
      VersionStatus: versionStatus,
      UserID: JSON.parse(sessionStorage.getItem('sessionObjectlogin') || 'null')?.userdata?.userid,
    };
    this.ngxUiLoaderService.start();
    this._ruleEngineService.InsertAndUpdateRuleConfig(body).subscribe({
      next: () => {
        Object.assign(payload, {
          pushToQc: true,
          qcNote: this.qcNoteContent || '',
          qcAudit: this.qcAudit
        });
        this.latestRuleExpressions = payload;
        this.saveCurrentVersionNoteToServer(role, onAfterNoteSaved);
      },
      error: () => {
        this.ngxUiLoaderService.stop();
        this.toastr.error(errorMessage || 'Unable to update RuleConfig.', '', {
          timeOut: 4000,
          positionClass: 'toast-bottom-right',
        });
      },
    });
    //Here we are clearing the cache of the route to reflect the latest changes if making changes as Live
    if(versionStatus.toLowerCase() == "live"){
      this.getAllTheCache(this.routeGuid);
    }
  }

  /**
   * Helper to save the current version note to the backend.
   * Used by both QC and Approve flows to avoid duplicate code.
   */
  private saveCurrentVersionNoteToServer(role: string, onCompleted: () => void): void {
    const option = this.getSelectedVersionOption();
    const routeRuleMappingID = option?.routeRuleMappingID;
    const userID = JSON.parse(sessionStorage.getItem('sessionObjectlogin') || 'null')?.userdata?.userid;

    if (routeRuleMappingID == null) {
      onCompleted();
      return;
    }

    const notesBody = {
      RouteUniqueID: Number(this.uniqueID),
      RouteRuleMappingID: Number(routeRuleMappingID),
      Notes: this.qcNoteContent || null,
      UserID: userID,
      Role: role,
      CreatedDate: new Date().toISOString(),
    };

    this._ruleEngineService.InsertRuleConfigVersionNotes(notesBody).subscribe({
      next: () => onCompleted(),
      error: () => onCompleted(),
    });
  }

  /**
   * Loads version notes from API and sets qcAudit for display.
   * Call when opening the QC/Approve overlay or the Version Notes box.
   * @param onDone optional callback when load finishes (success or error); used to turn off notesLoading.
   */
  loadVersionNotes(onDone?: () => void): void {
    const option = this.getSelectedVersionOption();
    const routeRuleMappingID = option?.routeRuleMappingID;
    if (routeRuleMappingID == null) {
      // Clear qcAudit so we don't show another version's notes when selected version has no mapping.
      this.qcAudit = [];
      onDone?.();
      return;
    }
    this._ruleEngineService
      .GetRuleConfigVersionNotes({ routeUniqueID: this.uniqueID, routeRuleMappingID })
      .subscribe({
        next: (data: any) => {
          const list = Array.isArray(data)
            ? data
            : [];
          this.qcAudit = list.map((n: any) => {
            const rawTs = n.CreatedDate ?? '';
            const timestamp = this.formatRuleConfigDateForDisplay(rawTs) || rawTs;
            return {
              role: n.Role ?? '',
              notes: n.Notes ?? '',
              user: (n.UserID ?? '')?.toString() ?? '',
              timestamp,
            };
          });
          onDone?.();
        },
        error: () => {
          // Don't clear qcAudit on error  keep existing list so audit doesn't disappear.
          onDone?.();
        },
      });
  }

  /** Opens the Version Notes overlay, loads notes for the currently selected version, and shows them read-only. */
  openVersionNotesBox(): void {
    this.showVersionNotes = true;
    this.notesLoading = true;
    this.loadVersionNotes(() => (this.notesLoading = false));
  }

  /** Closes the Version Notes overlay. */
  closeVersionNotesBox(): void {
    this.showVersionNotes = false;
  }

  /** Opens Compare overlay. Left = second-to-last version, Right = latest */
  onClickComapare(): void {
    this.showCompareOverlay = true;
    if (this.versionOptions.length >= 2) {
      this.compareLeftVersion = this.versionOptions[this.versionOptions.length - 2].value;
      this.compareRightVersion = this.versionOptions[this.versionOptions.length - 1].value;
    } else if (this.versionOptions.length === 1) {
      this.compareLeftVersion = this.versionOptions[0].value;
      this.compareRightVersion = this.versionOptions[0].value;
    } else {
      this.compareLeftVersion = null;
      this.compareRightVersion = null;
    }
    this.refreshCompareDiff();
  }

  closeCompareOverlay(): void {
    this.showCompareOverlay = false;
  }

  /** Refreshes the diff when either version dropdown changes. */
  onCompareVersionChange(): void {
    this.refreshCompareDiff();
  }

  private getRuleConfigJsonForVersion(versionValue: string | null): string {
    if (!versionValue) return '{}';
    const option = this.versionOptions.find((o) => o.value?.toString() === String(versionValue));
    const configdata = option?.configdata;
    if (configdata == null) return '{}';
    try {
      const parsed = typeof configdata === 'string' ? JSON.parse(configdata) : configdata;
      return JSON.stringify(parsed, null, 2);
    } catch {
      return typeof configdata === 'string' ? configdata : JSON.stringify(configdata);
    }
  }

  private refreshCompareDiff(): void {
    const leftJson = this.getRuleConfigJsonForVersion(this.compareLeftVersion);
    const rightJson = this.getRuleConfigJsonForVersion(this.compareRightVersion);
    this.compareDiffOldModel = { ...this.compareDiffOldModel, code: leftJson };
    this.compareDiffNewModel = { ...this.compareDiffNewModel, code: rightJson };
  }

  get bothEntitiesAreTestOrganization(): boolean {
    return this.testOrgFlagsLoaded
      && this.sourceIsTestOrganization === '1'
      && this.destinationIsTestOrganization === '1';
  }

  private applyParentTestOrganizationFlags(): void {
    const source = this.ruleConfigRequest?.sourceIsTestOrganization;
    const destination = this.ruleConfigRequest?.destinationIsTestOrganization;
    if (source != null && source !== '' && destination != null && destination !== '') {
      this.sourceIsTestOrganization = source === '1' ? '1' : '0';
      this.destinationIsTestOrganization = destination === '1' ? '1' : '0';
      this.testOrgFlagsLoaded = true;
      return;
    }
    this.sourceIsTestOrganization = '0';
    this.destinationIsTestOrganization = '0';
    this.testOrgFlagsLoaded = true;
  }

  onClickSendToQC() {
    this.qcStage = 'qc';
    this.qcNoteContent = '';
    this.loadVersionNotes();
    this.showQcNote = true;
  }

  onClickApprove() {
    this.qcStage = 'approve';
    this.qcNoteContent = '';
    this.loadVersionNotes();
    this.showQcNote = true;
  }

  onClickReject() {
    this.qcStage = 'reject';
    this.qcNoteContent = '';
    this.loadVersionNotes();
    this.showQcNote = true;
  }

  closeQcNote() {
    this.showQcNote = false;
    this.qcStage = null;
  }

  /**
   * Refreshes version list and closes QC overlay. Used after QC save and after Approve.
   */
  private refreshVersionsAfterSave(
    currentNumeric: number,
    options?: {
      successMessage?: string;
      errorMessage?: string;
      isApproveReject?: boolean;
      isSendToQc?: boolean;
    }
  ): void {
    const opts = options || {};
    const successMessage = opts.successMessage ?? 'RuleConfig approved and moved to QC.';
    const errorMessage = opts.errorMessage ?? 'Unable to refresh versions.';
    const isApproveReject = opts.isApproveReject ?? false;
    const isSendToQc = opts.isSendToQc ?? false;

    this._ruleEngineService
      .GetRuleConfigVersions(this.uniqueID, this.ruleExecutionStage)
      .subscribe({
        next: (data: any) => {
          const raw = Array.isArray(data) ? data : [];
          this.applyVersionList(raw, currentNumeric);
          this.ngxUiLoaderService.stop();
          this.toastr.success(successMessage, '', {
            timeOut: 2000,
            positionClass: 'toast-bottom-right',
          });
          this.isApproveReject = isApproveReject;
          this.isSendToQc = isSendToQc;
          this.showQcNote = false;
          this.qcStage = null;
        },
        error: () => {
          this.ngxUiLoaderService.stop();
          this.toastr.error(errorMessage, '', {
            timeOut: 3000,
            positionClass: 'toast-bottom-right',
          });
          this.isApproveReject = isApproveReject;
          this.isSendToQc = isSendToQc;
          this.showQcNote = false;
          this.qcStage = null;
        },
      });
  }

  saveRuleVersionNote() {
    const payload = this.latestRuleExpressions ?? this.buildRuleExpressionsPayload();
    if (!payload) {
      this.toastr.warning('Please save rules first.', '', {
        timeOut: 3000,
        positionClass: 'toast-bottom-right',
      });
      return;
    }

    const lastVersionValue = this.versionOptions.length
      ? parseInt(this.versionOptions[this.versionOptions.length - 1].value as any, 10)
      : 1;
    const currentNumeric =
      parseInt(this.selectedVersion as any, 10) || lastVersionValue;

    // Send to QC: update version to QC in DB, save note, refresh dropdown.
    if (this.qcStage === 'qc') {
      this.updateRuleConfigAndSaveNote({
        payload,
        version: currentNumeric,
        versionStatus: 'QC',
        role: 'QC',
        errorMessage: 'Unable to update version to QC.',
        onAfterNoteSaved: () => {
          this.addAuditEntry('QC', this.qcNoteContent || '(no note)');
          const auditLabel = this.getAuditRuleConfigLabel();
          this.commonService.auditsave(
            'Update',
            '',
            `${auditLabel} version qc approval is completed - ${currentNumeric} QC`,
            'Pipeline',
            this.uniqueID,
            `${auditLabel} Version Status Updated Successfully`
          ).then(() => { }).catch(() => { });
          this.refreshVersionsAfterSave(currentNumeric, {
            successMessage: 'Sent to QC Verification.',
            errorMessage: 'Sent to QC, but unable to refresh versions.',
            isApproveReject: true,
            isSendToQc: false,
          });
        },
      });
      return;
    }

    // Approval / rejection stage
    const role = this.qcStage === 'approve' ? 'Manager' : 'Manager (Rejected)';
    this.addAuditEntry(role, this.qcNoteContent || '(no note)');

    if (this.qcStage === 'approve') {
      Object.assign(payload, { approvalStatus: this.qcStage });
      this.updateRuleConfigAndSaveNote({
        payload,
        version: currentNumeric,
        versionStatus: 'Live',
        role: 'Manager',
        errorMessage: 'Unable to approve and make version Live. Please try again.',
        onAfterNoteSaved: () => {
          // PreValidation Live: server owns version audit then IsRuleEngineAck auto-enable audit
          const stage = (this.ruleExecutionStage ?? '').toString().trim().toLowerCase();
          if (stage !== 'prevalidation') {
            const auditLabel = this.getAuditRuleConfigLabel();
            this.commonService.auditsave(
              'Update',
              '',
              `${auditLabel} version is approved by manager - ${currentNumeric} Live`,
              'Pipeline',
              this.uniqueID,
              `${auditLabel} Version Updated Successfully`
            ).then(() => { }).catch(() => { });
          }
          this.refreshVersionsAfterSave(currentNumeric, {
            successMessage: 'Rule version is approved and made live successfully.',
            errorMessage: 'Approved to Live, but unable to refresh versions.',
            isApproveReject: true,
            isSendToQc: false,
          });
        },
      });
    } else {
      // Reject: persist note as version note as well, then close note popup.
      this.saveCurrentVersionNoteToServer('Manager', () => {
        const rejectedOption = this.getSelectedVersionOption();
        const rejectedStatus = (rejectedOption?.status ?? 'QC').toString().trim();
        const auditLabel = this.getAuditRuleConfigLabel();
        this.commonService.auditsave(
          'Update',
          '',
          `${auditLabel} is rejected by manager for version-${currentNumeric} ${rejectedStatus}`,
          'Pipeline',
          this.uniqueID,
          `${auditLabel} Version Status Updated Successfully`
        ).then(() => { }).catch(() => { });
        Object.assign(payload, {
          pushToQc: true,
          approvalStatus: this.qcStage,
          qcNote: this.qcNoteContent || '',
          qcAudit: this.qcAudit
        });

        this.latestRuleExpressions = payload;
        this.isApproveReject = false;
        this.showQcNote = false;
        this.qcStage = null;
        this.toastr.success('Reject note saved successfully.', '', {
          timeOut: 2000,
          positionClass: 'toast-bottom-right',
        });
      });
    }
  }

  //#region This method will generate the json required to render the build expression component
  prepareJsonFromDefaultRules() {
    if (this.rules.length > 0) {
      let expressionGroupObj = {};
      let expressionGroupArray = [];
      let expressionArray = [];
      this.rules.forEach((rule: Rule, index: number) => {
        if (index === 0) {
          let expressionGroup = {
            condition: "",
            rule: rule.rulename,
          };
          expressionArray.push(expressionGroup);
        } else {
          let expression = {
            condition: "AND",
            rule: rule.rulename,
          };
          expressionArray.push(expression);
        }
      });
      expressionGroupArray.push({
        expressionArray: expressionArray,
        groupOperator: "",
      });
      expressionGroupObj = {
        expressionGroupArray: expressionGroupArray,
      };
      this._ruleEngineService.holdTheBuildExpressionData = expressionGroupObj;
    }
  }
  //#endregion

  //#region This method prepares the JSON needed for showing the contents of the expression builder when the final rule expression is empty
  prepareTheJsonForEmptyFinalRuleExpression() {
      let expressionGroupObj = {};
      let expressionGroupArray = [];
      let expressionArray = [];
          let expressionGroup = {
            condition: "",
            rule: "",
          };
          expressionArray.push(expressionGroup);
          let expression = {
            condition: "",
            rule: "",
          };
          expressionArray.push(expression);
      expressionGroupArray.push({
        expressionArray: expressionArray,
        groupOperator: "",
      });
      expressionGroupObj = {
        expressionGroupArray: expressionGroupArray,
      };
      this._ruleEngineService.holdTheBuildExpressionData = expressionGroupObj;
    }
    //#endregion
  
  //#region This method will prepare the json needed to render the build expression component by using the final rule expression
  prepareJsonFromFinalExp(
    ruleSatisfaction: string
  ) {
    const regexSingleRule = /^\(\s*[a-zA-Z0-9_]+(?:\s+(?!AND|OR\b)[a-zA-Z0-9_]+)*\s*\)$/i;
    const regexAndOrRule =
      /^\(\s*[a-zA-Z0-9_ ]+(\s+(AND|OR)\s+[a-zA-Z0-9_ ]+)+\s*\)$/i;
    const regexMultipleAndOr = /^\(\s*[a-zA-Z0-9_ ]+(\s*(AND|OR)\s*[a-zA-Z0-9_ ]+)?\s*\)\s*(AND|OR)\s*\(\s*[a-zA-Z0-9_ ]+(\s*(AND|OR)\s*[a-zA-Z0-9_ ]+)?\s*\)(\s*(AND|OR)\s*\(\s*[a-zA-Z0-9_ ]+(\s*(AND|OR)\s*[a-zA-Z0-9_ ]+)?\s*\))*$/i;
    let formValue;

    if (regexSingleRule.test(ruleSatisfaction.trim())) {
      formValue = this.generateJsonForFormForSingleRule(ruleSatisfaction);
    } else if (regexAndOrRule.test(ruleSatisfaction.trim())) {
      formValue = this.generateJsonForFormForAndOrRule(ruleSatisfaction);
    } else if (regexMultipleAndOr.test(ruleSatisfaction.trim())) {
      formValue = this.generateJsonForFormForMultipleAndOr(ruleSatisfaction);
    }  
    this._ruleEngineService.holdTheBuildExpressionData = formValue;
  }
  

  generateJsonForFormForSingleRule(ruleSatisfaction: string) {
    const rule = ruleSatisfaction.replace(/[()]/g, "").trim();
    let formValue = {
      expressionGroupArray: [
        {
          expressionArray: [{ condition: "", rule: rule }],
          groupOperator: "",
        },
      ],
    };
    return formValue;
  }
  generateJsonForFormForAndOrRule(ruleSatisfaction: string) {
    // Remove outer parentheses and trim spaces
    const innerExpression = ruleSatisfaction.trim().slice(1, -1);
    // Split based on AND or OR while preserving operators
    const tokens = innerExpression.split(/\s+(AND|OR)\s+/i);

    let expressionArray: { condition: string; rule: string }[] = [];
    let currentCondition = "";

    for (const token of tokens) {
      if (token.toUpperCase() === "AND" || token.toUpperCase() === "OR") {
        currentCondition = token.toUpperCase(); // Store the operator
      } else {
        expressionArray.push({ condition: currentCondition, rule: token });
        currentCondition = ""; // Reset after applying
      }
    }

    return {
      expressionGroupArray: [
        {
          expressionArray,
          groupOperator: "",
        },
      ],
    };
  }
  generateJsonForFormForMultipleAndOr(ruleSatisfaction: string) {
    const regexGroup =
      /\(\s*[a-zA-Z0-9_ ]+(\s*(AND|OR)\s*[a-zA-Z0-9_ ]+)?\s*\)/gi;
    const regexOperator = /\)\s*(AND|OR)\s*\(/gi;

    // Extract groups and operators
    const groups = ruleSatisfaction.match(regexGroup) || [];
    const operators = [...ruleSatisfaction.matchAll(regexOperator)].map(
      (match) => match[1]
    );

    const expressionGroupArray = groups.map((group, index) => {
      // Use the generateJsonForAndOrRule logic for each group
      const { expressionGroupArray } =
        this.generateJsonForFormForAndOrRule(group);

      // Dynamically assign the groupOperator for each group
      return {
        ...expressionGroupArray[0], // Spread the expression array and groupOperator structure
        groupOperator: index < operators.length ? operators[index] : "", // Assign operator between groups
      };
    });

    return { expressionGroupArray };
  }
//#endregion
  buildFinalExpression() {
    const jsonDataToBuildFinalExpressoin =
      this._ruleEngineService.holdTheBuildExpressionData.expressionGroupArray;
    let finalstr = jsonDataToBuildFinalExpressoin
      .map((group: any, index: any) => {
        let innerExpression = group.expressionArray
          .map(
            (exp: any, rowIdx: number) =>
              `${rowIdx > 0 && exp.condition ? exp.condition + " " : ""}${exp.rule}`
          )
          .join(" ");
        // Wrap the inner expression in parentheses
        return `(${innerExpression.trim()})${
          index < jsonDataToBuildFinalExpressoin.length - 1 &&
          group.groupOperator
            ? ` ${group.groupOperator}`
            : ""
        }`;
      })
      .join(" ");
    this._ruleEngineService.setFinalRuleExpression(finalstr);
  }

  viewSavedRules(rulesData) {
    const operationMap: { [key: string]: string } = {
      'notin': 'NOT IN',
      'in': 'IN',
      'contains': 'CONTAINS'
    };
    rulesData[0].rules.forEach((rule: any) => {
      let transformedOperator = operationMap[rule.operation.toLowerCase()] || rule.operation; // Use the mapping or keep original
        let ruleToAdd :Rule = {
          rulenumber: rule.rulenumber,
          rulename: rule.rulename,
          value: rule.value,
          field: rule.field,
          operator: transformedOperator,
          ruletype: rule.ruletype,
          param: rule.param
        };
        this.rules.push(ruleToAdd);
    });
    this.finalExpression = this.convertTheNumberRuleExpToNamedRuleExp(
      this.ruleConfigRequest.data[0].ruleSatisfaction,
      this.rules
    );
    this.loadPreValidationFieldsFromConfig(rulesData[0]);
  }

  openPredicateDialog() {
    const dialogRef = this.dialog.open(PredicateFilterComponent, {
      height: "85%",
      width: "90%",
      maxWidth: "18vw !important",
      data: {},
      disableClose: true,
      panelClass: "Predicate-dialog-container",
      autoFocus: false,
    });
    dialogRef.afterClosed().subscribe(() => {
      this.isPopUp = false;
    });
  }

  onTemplateClose() {
    this.opentemplate = false;
  }
  onClickOpenTemplate(event: any) {
    this.opentemplate = true;
  }

  //#region This is responsible for converting the rule expression with rule numbers into rule expression with rule names
  convertTheNumberRuleExpToNamedRuleExp(
    finalNumberRuleExp: string,
    rules: any
  ) {
    return convertNumberedRuleExpressionToNames(finalNumberRuleExp, rules);
  }
  //#endregion

  //#region This is responsible for converting the rule expression with rule names into rule expression with rule numbers
  convertTheNamedRuleExpToNumberRuleExp(
    finalNameRuleExp: string,
    rules: any
  ) {
    return convertNamedRuleExpressionToNumbers(finalNameRuleExp, rules);
  }
  //#endregion

  
  copyToClip(event: any, message: string) {
    if (event != "") {
      copyToClipboard(event);
    }
    this.toastr.success(message + " copied", "", {
      timeOut: 500,
      positionClass: "toast-bottom-right",
    });
  }

  /** ADDED: toggle non-blocking backdrop + outside-click close for Version mat-select (Rule Builder header). */
  onVersionSelectOpenedChange(opened: boolean): void {
    if (opened) {
      document.body.classList.add(
        RuleConfigComponentComponent.VERSION_SELECT_BODY_CLASS
      );
      this.attachVersionSelectOutsideClickListener();
    } else {
      this.cleanupVersionSelectOverlayState();
    }
  }

  private attachVersionSelectOutsideClickListener(): void {
    this.detachVersionSelectOutsideClickListener();
    this.versionSelectOutsideClickListener = (event: Event) => {
      const target = event.target as Node | null;
      if (!target || !this.versionSelectRef) {
        return;
      }
      const triggerEl = this.versionSelectRef._elementRef
        ?.nativeElement as HTMLElement | undefined;
      if (triggerEl?.contains(target)) {
        return;
      }
      const panels = document.querySelectorAll(
        `.${RuleConfigComponentComponent.VERSION_SELECT_PANEL_CLASS}`
      );
      for (let i = 0; i < panels.length; i++) {
        if (panels[i].contains(target)) {
          return;
        }
      }
      if (this.versionSelectRef.panelOpen) {
        this.versionSelectRef.close();
      }
    };
    setTimeout(() => {
      if (this.versionSelectOutsideClickListener) {
        document.addEventListener(
          "click",
          this.versionSelectOutsideClickListener,
          true
        );
        document.addEventListener(
          "touchend",
          this.versionSelectOutsideClickListener,
          true
        );
      }
    }, 0);
  }

  private detachVersionSelectOutsideClickListener(): void {
    if (this.versionSelectOutsideClickListener) {
      document.removeEventListener(
        "click",
        this.versionSelectOutsideClickListener,
        true
      );
      document.removeEventListener(
        "touchend",
        this.versionSelectOutsideClickListener,
        true
      );
      this.versionSelectOutsideClickListener = null;
    }
  }

  private cleanupVersionSelectOverlayState(): void {
    document.body.classList.remove(
      RuleConfigComponentComponent.VERSION_SELECT_BODY_CLASS
    );
    this.detachVersionSelectOutsideClickListener();
  }
}